import { AnyAction } from 'redux';
import { Media } from '@synergy/models';
import { UploadState } from '@synergy/models';

import {
    UPLOAD_FILES_RESET,
    UPLOAD_FILES_SET,
    UPLOAD_FILE_CANCEL,
    UPLOAD_FILE_FAILURE,
    UPLOAD_FILE_PROGRESS,
    UPLOAD_FILE_REMOVE,
    UPLOAD_FILE_START,
    UPLOAD_FILE_SUCCESS,
    UPLOAD_FILE_TITLE,
} from './media-actions';

export interface MediaReducerState {
    flat: Media[];
}

const initialMediaReducerState: MediaReducerState = {
    flat: [],
};

const replaceMedia = (newMedia: Media, existingMedia?: Media[]) => {
    if (!existingMedia) {
        return [newMedia];
    }

    const oldMedia = existingMedia.find(
        searchMedia =>
            searchMedia.pathname === newMedia.originalName &&
            searchMedia.size === newMedia.size &&
            searchMedia.id === -1
    );
    if (oldMedia) {
        const index = existingMedia.indexOf(oldMedia);
        if (index >= 0) {
            const clone = [...existingMedia];
            clone[index] = newMedia;
            return clone;
        }
    }
    return existingMedia;
};

const removeMedia = (media: Media, existingMedia?: Media[]): Media[] => {
    if (!existingMedia) {
        return [];
    }

    // File was uploaded and has id
    if (media.id > -1) {
        return existingMedia.filter(searchMedia => searchMedia.id !== media.id);
    }
    return existingMedia.filter(
        searchMedia =>
            !(searchMedia.pathname === media.originalName && searchMedia.size === media.size && searchMedia.id === -1)
    );
};

const finishMedia = (media: Media, existingMedia?: Media[]): Media[] => {
    media.state = UploadState.Finished;
    return replaceMedia(media, existingMedia);
};

const failedMedia = (media: Media, existingMedia?: Media[]): Media[] => {
    media.state = UploadState.Failed;
    return replaceMedia(media, existingMedia);
};

const progressMedia = (media: Media, progress: number, existingMedia?: Media[]): Media[] => {
    media.progress = progress;
    return replaceMedia(media, existingMedia);
};

export const MediaReducer = (
    state: MediaReducerState = initialMediaReducerState,
    action: AnyAction
): MediaReducerState => {
    const { type, ...rest } = action;
    switch (type) {
        case UPLOAD_FILE_START: {
            return {
                ...state,
                flat: [...state.flat, rest['media']],
            };
        }

        case UPLOAD_FILE_SUCCESS: {
            return {
                ...state,
                flat: finishMedia(rest['media'], state.flat),
            };
        }

        case UPLOAD_FILE_FAILURE: {
            return {
                ...state,
                flat: failedMedia(rest['media'], state.flat),
            };
        }

        case UPLOAD_FILE_PROGRESS: {
            return {
                ...state,
                flat: progressMedia(rest['media'], rest['progress'], state.flat),
            };
        }

        case UPLOAD_FILE_REMOVE: {
            return {
                ...state,
                flat: removeMedia(rest['media'], state.flat),
            };
        }

        case UPLOAD_FILE_CANCEL: {
            return {
                ...state,
                flat: removeMedia(rest['media'], state.flat),
            };
        }

        case UPLOAD_FILE_TITLE: {
            return {
                ...state,
                flat: state.flat.map(media =>
                    media.id === rest['id'] ? Media.fromPlainObject({ ...media, title: rest['title'] }) : media
                ),
            };
        }

        case UPLOAD_FILES_RESET: {
            return {
                ...state,
                flat: initialMediaReducerState.flat,
            };
        }

        case UPLOAD_FILES_SET: {
            return {
                ...state,
                flat: rest['media'],
            };
        }

        default: {
            return state;
        }
    }
};
